import {
  Children,
  type MouseEvent,
  type ReactElement,
  type ReactNode,
  cloneElement,
  isValidElement,
  useCallback,
  useState,
} from "react";

import { cx, styled } from "simplydo/react/styled";

import { Menu } from "../menu/Menu";
import type { MenuItemProps } from "../menu/Menu.types";
import { MenuItem } from "../menu/MenuItem";
import { StyledTab } from "./Tab.styles";
import type { TabPaneShorthand, TabProps } from "./Tab.types";
import { TabPane } from "./TabPane";

function toIndex(value: number | string | undefined, fallback = 0): number {
  if (value === undefined) return fallback;
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return (
    typeof value === "object" &&
    value !== null &&
    !Array.isArray(value) &&
    !isValidElement(value)
  );
}

function resolveMenuItem(
  menuItem: TabPaneShorthand["menuItem"],
  index: number,
  active: boolean,
  onSelect: (event: MouseEvent<HTMLElement>, index: number) => void,
): ReactNode {
  if (menuItem == null) {
    return null;
  }

  if (typeof menuItem === "string" || typeof menuItem === "number") {
    return (
      <MenuItem
        key={index}
        active={active}
        content={menuItem}
        onClick={(e) => onSelect(e, index)}
      />
    );
  }

  if (isValidElement(menuItem)) {
    const existingOnClick = (menuItem.props as MenuItemProps).onClick;
    return cloneElement(menuItem as ReactElement<MenuItemProps>, {
      key: menuItem.key ?? index,
      active,
      onClick: (event: MouseEvent<HTMLElement>, data: MenuItemProps) => {
        existingOnClick?.(event, data);
        onSelect(event, index);
      },
    });
  }

  if (isPlainObject(menuItem)) {
    return (
      <MenuItem
        key={index}
        active={active}
        {...(menuItem as MenuItemProps)}
        onClick={(e, data) => {
          (menuItem as MenuItemProps).onClick?.(e, data);
          onSelect(e, index);
        }}
      />
    );
  }

  return (
    <MenuItem key={index} active={active} onClick={(e) => onSelect(e, index)}>
      {menuItem as ReactNode}
    </MenuItem>
  );
}

const TabComponent = (props: TabProps): ReactElement => {
  const {
    activeIndex: activeIndexProp,
    defaultActiveIndex = 0,
    menu,
    panes,
    renderActiveOnly = true,
    onTabChange,
    children,
    className,
    style,
  } = props;

  const isControlled = activeIndexProp !== undefined;
  const [uncontrolledIndex, setUncontrolledIndex] = useState(() =>
    toIndex(defaultActiveIndex, 0),
  );
  const activeIndex = isControlled
    ? toIndex(activeIndexProp, 0)
    : uncontrolledIndex;

  const handleSelect = useCallback(
    (event: MouseEvent<HTMLElement>, index: number) => {
      if (!isControlled) {
        setUncontrolledIndex(index);
      }
      onTabChange?.(event, { ...props, activeIndex: index });
    },
    [isControlled, onTabChange, props],
  );

  if (panes) {
    const menuItems = panes.map((pane, index) =>
      resolveMenuItem(
        pane.menuItem,
        index,
        activeIndex === index,
        handleSelect,
      ),
    );

    const paneNodes = panes.map((pane, index) => {
      const isActive = activeIndex === index;
      if (renderActiveOnly && !isActive) {
        return null;
      }

      let content: ReactNode = null;
      if (pane.render) {
        content = pane.render();
      } else if (pane.pane != null) {
        if (isValidElement(pane.pane)) {
          content = pane.pane;
        } else if (isPlainObject(pane.pane)) {
          content = <TabPane {...(pane.pane as object)} active={isActive} />;
        } else {
          content = (
            <TabPane active={isActive}>{pane.pane as ReactNode}</TabPane>
          );
        }
      }

      if (
        renderActiveOnly &&
        isValidElement(content) &&
        content.type === TabPane
      ) {
        return cloneElement(content as ReactElement<{ active?: boolean }>, {
          key: index,
          active: true,
        });
      }

      return (
        <div key={index} style={renderActiveOnly ? undefined : undefined}>
          {content}
        </div>
      );
    });

    return (
      <StyledTab className={cx(className)} style={style}>
        <Menu {...(menu || { tabular: true })}>{menuItems}</Menu>
        {paneNodes}
      </StyledTab>
    );
  }

  // Children mode: Tab.Pane children; optional first-child Menu not used —
  // callers typically use panes. Support Pane children with activeIndex.
  const childArray = Children.toArray(children);
  return (
    <StyledTab className={cx(className)} style={style}>
      {childArray.map((child, index) => {
        if (!isValidElement(child)) return child;
        if (
          child.type === TabPane ||
          (child.type as { displayName?: string }).displayName === "TabPane"
        ) {
          const isActive = activeIndex === index;
          if (renderActiveOnly && !isActive) return null;
          return cloneElement(child as ReactElement<{ active?: boolean }>, {
            key: child.key ?? index,
            active: isActive,
          });
        }
        return child;
      })}
    </StyledTab>
  );
};

export const Tab = styled(TabComponent)``;
